import { DestroyRef, effect, inject, Injectable, Injector, signal, untracked } from '@angular/core';
import {
  createHintCardOverlay,
  isAnchorUnderModal,
  onOverlayLayerChange,
  type HintCardOverlay,
} from '@lite-workspace/shared/helm-ui/hint';
import { clipLessonId, type ActiveHint } from '../catalog.types';
import { LIBRARY_DOOR_HINT, OnboardingService } from '../onboarding.service';
import { CoachCardComponent } from './coach-card.component';

interface OpenCoach {
  id: string;
  anchor: HTMLElement;
  overlay: HintCardOverlay<CoachCardComponent>;
  /** Hover host kept revealed while the card points into it (`app-chrome.css`). */
  host: HTMLElement;
}

/** Marks the anchor's hover-reveal host so its hover-only controls stay shown under the card. */
const COACH_OPEN_ATTR = 'data-coach-open';

/** Карточка на экране, как её видит хост: запись и элемент, под которым она стоит. */
export interface ShownCoach {
  id: string;
  anchor: HTMLElement;
}

/**
 * Короткое ожидание устоявшегося экрана между карточками одного визита: якорь следующей карточки
 * мог ещё переезжать. Отсчёт идёт заново на каждой смене подсказки. Глазом это не пауза — карточки
 * идут одна за другой.
 */
export const COACH_CARD_DELAY_MS = 500;

/**
 * Первая карточка визита на экран ждёт, пока экран соберётся: якоря встают вразнобой, и поздняя
 * запись, чей якорь встал раньше, на секунду оказывается головой очереди — её карточка мелькнула бы
 * и сменилась настоящей. Отсчёт так же идёт заново на каждой смене подсказки.
 */
export const COACH_SCREEN_ENTRY_MS = 5000;

/** Въезд карточки на новом якоре: одна короткая анимация показа, без движения у тех, кто его отключил. */
const JUMP_IN_CLASSES = ['motion-safe:animate-in', 'motion-safe:fade-in-0', 'motion-safe:zoom-in-95', 'duration-[var(--t-fast)]'];

/** «Fully on screen» with slack: under fractional zoom Chromium reports 0.99… for a fully visible box. */
const FULLY_SHOWN = 0.99;

/** Share of the viewport a `fixed` node must cover on both axes to count as a page layer. */
const PAGE_LAYER_COVER = 0.9;

/**
 * A full-screen page layer (faces walk, verify mode) is a `fixed` element, not a CDK pane: the overlay
 * container never hears of it, and the card's CDK tier would paint over it. Ask the page what sits on
 * the anchor's centre instead.
 * ponytail: read on each `sync` only — a closed layer brings the card back at the next sync; observe the layer if that lags.
 */
function isAnchorUnderPageLayer(anchor: HTMLElement): boolean {
  // jsdom has no layout: no point, no cover.
  if (typeof document.elementFromPoint !== 'function') return false;

  const rect = anchor.getBoundingClientRect();
  let node = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
  if (!node || anchor.contains(node) || node.closest('.cdk-overlay-container')) return false;

  for (; node; node = node.parentElement) {
    if (getComputedStyle(node).position !== 'fixed') continue;
    // A toast or an edge bar is `fixed` too and leaves without a re-sync: only a viewport-wide layer hides the card.
    const { width, height } = node.getBoundingClientRect();
    if (width >= innerWidth * PAGE_LAYER_COVER && height >= innerHeight * PAGE_LAYER_COVER) return !node.contains(anchor);
  }

  return false;
}

/**
 * Показ карточки коуча: движок говорит, какая подсказка сейчас живая, сервис держит под её якорем
 * карточку. Слой оверлея целиком в helm-ui — эта библиотека про CDK не знает.
 */
@Injectable({ providedIn: 'root' })
export class CoachOverlayService {
  private readonly onboarding = inject(OnboardingService);
  private readonly injector = inject(Injector);

  /** Чего хочет движок. Показана подсказка или ждёт освобождения якоря — решает `sync`. */
  private hint: ActiveHint | null = null;
  private open: OpenCoach | null = null;
  /**
   * Карточка, которая ДЕЙСТВИТЕЛЬНО стоит на экране, — не та, которую назвала очередь. Между ними
   * ожидание устоявшегося экрана, чужие окна и незарегистрированные якоря: хост, доводящий своё
   * место до вида карточки, обязан ждать показа, а не головы очереди.
   */
  private readonly shownState = signal<ShownCoach | null>(null);
  readonly shown = this.shownState.asReadonly();
  /** Подсказка простояла неизменной свой отсчёт: до этого `sync` карточку не открывает. */
  private settled = false;
  /**
   * Экран текущего визита и была ли на нём уже карточка: первая ждёт сборки экрана, остальные — нет.
   */
  private visitScreen: string | null | undefined;
  private shownOnVisit = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  /** Нажали «Назад» или «Дальше»: следующая карточка встаёт сразу и въезжает анимацией. */
  private jumping = false;
  /**
   * A queue card first shows only once its anchor is fully on screen: a row below the fold waits for
   * the scroll. A press, a replay or a jump shows at once — the jump scrolls the anchor in itself.
   */
  private needsView = true;
  private inView = true;
  private watched: HTMLElement | undefined;
  private readonly viewport =
    typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(
          entries => {
            const entry = entries[entries.length - 1];
            const { height } = entry.boundingClientRect;
            // An anchor taller than the viewport never shows whole: any visible part of it counts.
            const tall = height > (entry.rootBounds?.height ?? Infinity);
            // Height, not area: an anchor clipped sideways (tab half off a scrolling nav) is still readable.
            this.inView = entry.isIntersecting && (tall || entry.intersectionRect.height >= height * FULLY_SHOWN);
            this.sync();
          },
          // ponytail: a sideways-clipped anchor tops out below 0.99, so only the first callback and 0-crossings judge it.
          { threshold: [0, FULLY_SHOWN] },
        )
      : null;

  constructor() {
    effect(() => {
      const next = this.onboarding.activeHint();
      const same = next?.record.id === this.hint?.record.id && next?.element === this.hint?.element;
      this.hint = next;
      // Сверка на каждой смене подсказки, и на её уходе тоже: ушёл на экран без карточек и вернулся —
      // это новый визит.
      const screen = untracked(() => this.onboarding.currentScreen());
      if (screen !== this.visitScreen) {
        this.visitScreen = screen;
        this.shownOnVisit = false;
      }

      // Карточку попросили нажатием на «?»: ждать нечего — фотограф ждёт ответа на свой вопрос сейчас.
      const requested = !!next && next.record.id === this.onboarding.requestedHint();
      // Повтор подсказок экрана — такая же просьба: экран давно собран, и обход начинается сразу.
      const replaying = this.onboarding.replaying();

      // Перескок по кнопкам — не новая подсказка на устоявшемся экране: ждать нечего, карточка
      // переезжает на соседний якорь тем же нажатием.
      if (requested || replaying || (!same && this.jumping)) this.settleNow();
      else if (!same) this.restartDelay();
      if (!same) this.needsView = !(requested || replaying || this.jumping);
      this.watch(next?.element);
      this.sync();
      this.applySteps();
      this.jumping = false;
    });

    const stopWatching = onOverlayLayerChange(this.injector, () => this.sync());

    inject(DestroyRef).onDestroy(() => {
      stopWatching();
      this.viewport?.disconnect();
      clearTimeout(this.timer);
      this.close();
    });
  }

  /** Новая подсказка (или её уход) — отсчёт с нуля: показывать нечего, пока экран не устоялся. */
  private restartDelay(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
    this.settled = false;
    if (!this.hint) return;

    this.timer = setTimeout(
      () => {
        this.settled = true;
        this.sync();
      },
      this.shownOnVisit ? COACH_CARD_DELAY_MS : COACH_SCREEN_ENTRY_MS,
    );
  }

  /** Observe the live hint's anchor; no observer (test DOM) = always in view. */
  private watch(element: HTMLElement | undefined): void {
    if (!this.viewport || element === this.watched) return;

    this.viewport.disconnect();
    this.watched = element;
    this.inView = false;
    if (element) this.viewport.observe(element);
  }

  /** Нажатие «Назад»/«Дальше»: движок переводит очередь, а показ соседней карточки идёт сразу. */
  private jump(move: () => void): void {
    this.jumping = true;
    move();
  }

  /** Перескок по кнопкам: показывать сразу — экран тот же, фотограф сам попросил соседнюю карточку. */
  private settleNow(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
    this.settled = true;
  }

  /** Счётчик и доступность кнопок живой карточки: очередь экрана может укоротиться под ней. */
  private applySteps(card = this.open?.overlay.component): void {
    const position = this.onboarding.hintPosition();
    const canPrev = this.onboarding.canPrev();
    const canNext = this.onboarding.canNext();

    card?.setInput('position', position);
    card?.setInput('canPrev', canPrev);
    card?.setInput('canNext', canNext);
  }

  /**
   * Карточка ждёт свободный якорь. Подсказка приходит через несколько секунд после загрузки, и к этому
   * моменту фотограф уже мог открыть окно: всплыв над чужим окном, карточка закрыла бы собой его крестик.
   * Пока якорь под подложкой — карточки нет; окно закрылось — она появляется, окно открылось поверх — уходит.
   * Якорь внутри самого окна — законный случай: там карточка часть окна и стоит над ним.
   * Библиотека уроков — не чужое окно: открыта из шапки или с самой карточки, она оставляет живую
   * карточку ждать возврата на месте.
   */
  private sync(): void {
    const live = this.settled ? this.hint : null;
    const free =
      live &&
      ((!isAnchorUnderModal(this.injector, live.element) && !isAnchorUnderPageLayer(live.element)) || this.keptUnderLibrary(live))
        ? live
        : null;

    // Пересчёт computed отдаёт новый объект на каждое движение зависимостей: без сверки по паре
    // id + элемент живая карточка пересобиралась бы на ровном месте и мигала.
    if (this.open?.id === free?.record.id && this.open?.anchor === free?.element) return;

    this.close();
    if (free && (this.inView || !this.needsView)) this.show(free);
  }

  /**
   * Открытая библиотека уроков карточку не снимает: та стоит на своём якоре под подложкой окна и
   * возвращается фотографу нетронутой. Только уже живая — новую под окном поднимать нельзя: слой
   * оверлея встаёт последним и накрыл бы собой окно.
   */
  private keptUnderLibrary(hint: ActiveHint): boolean {
    return this.onboarding.libraryRequest() !== null && this.open?.id === hint.record.id && this.open.anchor === hint.element;
  }

  private show({ record, element }: ActiveHint): void {
    // Место могло остаться в свёрнутом блоке (аккордеон настроек): блок, поймав это событие изнутри
    // своего тела, раскрывается сам. Карточка встаёт сразу и едет за якорем, пока блок раскрывается.
    element.dispatchEvent(new CustomEvent('onboarding-reveal', { bubbles: true }));
    // На якоре записи может открываться своё окно (права ученика у замочка ленты): такая карточка
    // уступает ему место у якоря и встаёт сбоку — иначе окно закрывает её собой.
    const overlay = createHintCardOverlay(this.injector, CoachCardComponent, element, 'bottom', record.coach?.aside ?? false);
    const card = overlay.component;

    card.setInput('title', record.coach?.title ?? '');
    card.setInput('body', record.coach?.body ?? '');
    const actions = record.coach?.actions ?? [];
    card.setInput('actions', actions);
    card.setInput('video', record.video);
    // Один id на обложку, длительность и переход в библиотеку: свой ролик записи либо чужой урок,
    // названный каталогом. По нему карточка сама берёт обложку и длительность из манифеста медиа.
    const lesson = clipLessonId(record);
    // Дверь в библиотеку — карточка с обложкой без ролика: нажатие на обложку открывает саму
    // библиотеку, и заводить там нечего (R52).
    const door = record.id === LIBRARY_DOOR_HINT;

    card.setInput('lessonId', lesson);
    card.setInput('door', door);
    this.applySteps(card);

    // Перескок видно глазом: карточка въезжает на новом якоре, а не просто возникает там.
    if (this.jumping) (card.location.nativeElement as HTMLElement).classList.add(...JUMP_IN_CLASSES);

    card.instance.prev.subscribe(() => this.jump(() => this.onboarding.prevHint()));
    card.instance.next.subscribe(() => this.jump(() => this.onboarding.nextHint()));

    card.instance.closed.subscribe(() => this.onboarding.dismissScreen(record.id));
    // Главная кнопка проходит запись и отдаёт действие хосту; остальные («Понятно») скрывают её навсегда.
    card.instance.action.subscribe(index =>
      actions[index]?.primary ? this.onboarding.completeAction(record.id) : this.onboarding.dismiss(record.id),
    );
    // Ролик уводит в библиотеку, но обход не двигает: карточка ждёт под окном и возвращается к
    // фотографу на своём месте — урок он только открыл, а не прошёл подсказку.
    card.instance.mediaClicked.subscribe(() =>
      door ? this.onboarding.openLibrary() : this.onboarding.openLibrary(lesson, { play: true }),
    );

    // Перескок по кнопкам: соседний якорь легко стоит у нижнего края экрана или за ним, и карточка
    // вставала бы в обрез. Подводим якорь к середине экрана; карточка едет следом сама — оверлей
    // подсказки пересчитывает место на каждом скролле. Первое появление не скроллит: экран фотограф
    // ещё не трогал, и увозить его нельзя.
    if (this.jumping) element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const host = element.closest<HTMLElement>('.group') ?? element;
    host.setAttribute(COACH_OPEN_ATTR, '');
    this.open = { id: record.id, anchor: element, overlay, host };
    this.shownState.set({ id: record.id, anchor: element });
    this.shownOnVisit = true;
    document.addEventListener('pointerdown', this.pressOutside, true);
    document.addEventListener('keydown', this.pressEscape);
  }

  /**
   * Escape closes the card the same way a press outside it does. A window stacked over the card
   * (lesson library) owns its own Escape: focus inside it leaves the card waiting underneath.
   */
  private readonly pressEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.open) return;

    const card = this.open.overlay.component.location.nativeElement as HTMLElement;
    const target = event.target;
    if (target instanceof Node && !card.contains(target)) {
      if (card.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) return;
    }

    if (this.onboarding.pressedHint() === this.open.id) this.onboarding.releaseHint(this.open.id);
    else this.onboarding.dismissScreen(this.open.id);
  };

  /**
   * Нажатие мимо карточки убирает её так же, как крестик. Подложки нет: нажатие доходит до того, во
   * что попало, — радио переключается тем же кликом. Мимо — это не сама карточка, не её место и не
   * якорь записи: их нажатие ведёт свой обработчик, и карточка закрывается ровно один раз. И не то,
   * что стоит в документе после карточки: слой оверлеев держит окна в порядке показа, и всё, что
   * позже неё, нарисовано поверх — библиотека, открытая с ролика, оставляет карточку ждать под собой.
   * Карточка, открытая нажатием «?», уходит без записи на сервер, как при уходе с экрана: нажатие мимо
   * часто и есть этот уход (ссылка в шапке редактора). Нажатие в другой «?» её не трогает — его просьба
   * сама сменит карточку; отпусти её здесь, и под этим «?» встала бы голова тура, закрытая его же кликом.
   */
  private readonly pressOutside = (event: PointerEvent): void => {
    const target = event.target;
    if (!this.open || !(target instanceof Node)) return;

    const card = this.open.overlay.component.location.nativeElement as HTMLElement;
    const anchor = this.onboarding.anchorOf(this.open.id);
    if (card.contains(target) || this.open.anchor.contains(target) || anchor?.contains(target)) return;
    if (card.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) return;

    if (this.onboarding.pressedHint() === this.open.id) {
      if (!this.onboarding.helpContains(target)) this.onboarding.releaseHint(this.open.id);
      return;
    }

    this.onboarding.dismissScreen(this.open.id);
  };

  private close(): void {
    if (!this.open) return;

    document.removeEventListener('pointerdown', this.pressOutside, true);
    document.removeEventListener('keydown', this.pressEscape);
    this.open.host.removeAttribute(COACH_OPEN_ATTR);
    this.open.overlay.destroy();
    this.open = null;
    this.shownState.set(null);
  }
}
